// YieldComparison.js — Big yield tiles + placeholder for 3-line recharts chart.
// Shows T-bill yield, CHF benchmark yield, and spread.

import React, { useMemo } from 'react';
import { TrendingUp, BarChart3 } from 'lucide-react';

function fmtPct(value) {
  if (value == null) return '--';
  return `${parseFloat(value).toFixed(2)}%`;
}

function fmtBps(value) {
  if (value == null) return '--';
  return `${(parseFloat(value) * 100).toFixed(0)} bps`;
}

export default function YieldComparison({ experiment, latestMark, marks = [] }) {
  const tbillYield = useMemo(() => {
    if (experiment?.tbill_yield != null) return parseFloat(experiment.tbill_yield);
    if (latestMark?.tbill_yield_364 != null) return parseFloat(latestMark.tbill_yield_364);
    return null;
  }, [experiment, latestMark]);

  const chfBenchmark = useMemo(() => {
    if (experiment?.chf_benchmark_yield != null) return parseFloat(experiment.chf_benchmark_yield);
    if (latestMark?.snb_policy_rate != null) return parseFloat(latestMark.snb_policy_rate);
    return null;
  }, [experiment, latestMark]);

  const spread = useMemo(() => {
    if (tbillYield == null || chfBenchmark == null) return null;
    return tbillYield - chfBenchmark;
  }, [tbillYield, chfBenchmark]);

  // Derive hedged return estimate from experiment data
  const hedgedReturn = useMemo(() => {
    if (experiment?.hedged_return_estimate != null) return parseFloat(experiment.hedged_return_estimate);
    if (tbillYield != null && experiment?.forward_premium_annualised != null) {
      return tbillYield - parseFloat(experiment.forward_premium_annualised);
    }
    return null;
  }, [experiment, tbillYield]);

  // Build sparkline data from marks for the chart placeholder
  const chartData = useMemo(() => {
    if (!marks || marks.length === 0) return [];
    return marks.slice(-30).map(m => ({
      date: m.mark_date,
      kesYield: m.tbill_yield_364 != null ? parseFloat(m.tbill_yield_364) : null,
      chfBenchmark: m.snb_policy_rate != null ? parseFloat(m.snb_policy_rate) : null,
      hedgedReturn: m.hedged_return_estimate != null ? parseFloat(m.hedged_return_estimate) : null,
    }));
  }, [marks]);

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-4 h-4" style={{ color: '#4D4D4D' }} />
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
          Yield Comparison
        </span>
      </div>

      {/* Big tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        {/* T-bill yield */}
        <div className="border border-zinc-200 p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">
            T-Bill Yield (364d)
          </p>
          <p className="font-mono text-2xl font-bold tabular-nums text-zinc-900">
            {fmtPct(tbillYield)}
          </p>
          <p className="font-mono text-[9px] text-zinc-400 mt-1">
            KES government T-bill
          </p>
        </div>

        {/* CHF benchmark */}
        <div className="border border-zinc-200 p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">
            CHF Benchmark Yield
          </p>
          <p className="font-mono text-2xl font-bold tabular-nums text-zinc-900">
            {fmtPct(chfBenchmark)}
          </p>
          <p className="font-mono text-[9px] text-zinc-400 mt-1">
            SNB SARON / GMBF rate
          </p>
        </div>

        {/* Spread */}
        <div className="border border-zinc-200 p-4" style={{ borderColor: '#4D4D4D' }}>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">
            Spread
          </p>
          <p className={`font-mono text-2xl font-bold tabular-nums ${
            spread != null && spread > 0 ? 'text-emerald-600' : 'text-zinc-900'
          }`}>
            {spread != null ? fmtPct(spread) : '--'}
          </p>
          <p className="font-mono text-[9px] text-zinc-400 mt-1">
            {spread != null ? fmtBps(spread) : '--'}
          </p>
        </div>
      </div>

      {/* Hedged return tile */}
      {hedgedReturn != null && (
        <div className="bg-zinc-50 border border-zinc-200 p-3 mb-5">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              Est. Hedged Return (CHF)
            </span>
            <span className={`font-mono text-sm font-semibold tabular-nums ${
              hedgedReturn > 0 ? 'text-emerald-600' : hedgedReturn < 0 ? 'text-red-600' : 'text-zinc-900'
            }`}>
              {fmtPct(hedgedReturn)}
            </span>
          </div>
        </div>
      )}

      {/* Chart placeholder */}
      <div className="border-t border-zinc-200 pt-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-3">
          Yield History (30 Days)
        </p>
        {chartData.length >= 2 ? (
          <div className="bg-zinc-50 border border-zinc-200 rounded-none p-4 h-48 flex items-center justify-center">
            {/* Recharts integration placeholder */}
            <div className="text-center">
              <BarChart3 className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
              <p className="font-mono text-[10px] text-zinc-400">
                3-line chart: KES yield, CHF benchmark, hedged return
              </p>
              <p className="font-mono text-[9px] text-zinc-400 mt-1">
                {chartData.length} data points available
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-zinc-50 border border-zinc-200 rounded-none p-4 h-48 flex items-center justify-center">
            <p className="font-mono text-[10px] text-zinc-400">
              Chart available after 2+ daily marks.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
